import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@trotroways/api";
import type { RouteStep } from "@/lib/api";

interface FareConfirmation {
	confirmedCount: number;
	label: string;
}

interface FareOverride {
	amount: number;
	reason?: string;
	timestamp: number;
}

export function useFareConfirmations(steps: RouteStep[]) {
	const [confirmations, setConfirmations] = useState<
		Record<string, FareConfirmation>
	>({});
	const [overrides, setOverrides] = useState<Record<string, FareOverride>>({});

	useEffect(() => {
		const routeIds = Array.from(
			new Set(steps.filter((s) => s.routeId).map((s) => s.routeId!)),
		);
		if (routeIds.length === 0) return;

		let cancelled = false;

		Promise.all(
			routeIds.map(async (routeId) => {
				try {
					const data = await apiClient.request<{ confirmed_count: number }>(
						`/fares/confirmations?route_id=${encodeURIComponent(routeId)}`,
						{ auth: "public" },
					);
					return {
						routeId,
						confirmedCount: Number((data as { confirmed_count: number }).confirmed_count) || 0,
					};
				} catch {
					return { routeId, confirmedCount: 0 };
				}
			}),
		).then((results) => {
			if (cancelled) return;
			const byRoute: Record<string, number> = {};
			for (const r of results) byRoute[r.routeId] = r.confirmedCount;

			const map: Record<string, FareConfirmation> = {};
			for (const step of steps) {
				const count = step.routeId ? byRoute[step.routeId] ?? 0 : 0;
				if (count > 0) {
					map[step.id] = {
						confirmedCount: count,
						label: "riders confirmed",
					};
				}
			}
			setConfirmations(map);
		});

		return () => {
			cancelled = true;
		};
	}, [steps]);

	const getConfirmation = useCallback(
		(stepId: string): FareConfirmation | null => {
			if (overrides[stepId]) return null;
			return confirmations[stepId] ?? null;
		},
		[confirmations, overrides],
	);

	const updateFare = useCallback(
		async (stepId: string, amount: number, reason?: string) => {
			setOverrides((prev) => ({
				...prev,
				[stepId]: { amount, reason, timestamp: Date.now() },
			}));

			const step = steps.find((s) => s.id === stepId);
			if (!step?.routeId) return false;

			try {
				await apiClient.request("/contributions/fare-reports", {
					auth: "public",
					method: "POST",
					body: JSON.stringify({
						route_id: step.routeId,
						from_stop_id: step.fromStopId,
						to_stop_id: step.toStopId,
						reported_fare: amount,
						notes: reason,
					}),
				});
				return true;
			} catch {
				return false;
			}
		},
		[steps],
	);

	const getEffectiveFare = useCallback(
		(stepId: string, originalFare?: number): number | null => {
			if (overrides[stepId]) return overrides[stepId].amount;
			return originalFare ?? null;
		},
		[overrides],
	);

	return { getConfirmation, updateFare, getEffectiveFare };
}